import { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export type PlanRow = { id: number; parent: number; detail: string };

export type ExplainRow = {
  addr: number;
  opcode: string;
  p1: number;
  p2: number;
  p3: number;
  p4: string;
  p5: string;
  comment: string;
};

type PlanTreeNode = PlanRow & { children: PlanTreeNode[]; depth: number };

type OperationType =
  | "scan"
  | "search_covering"
  | "search_index"
  | "auto_index"
  | "materialize"
  | "coroutine"
  | "temp_btree"
  | "compound"
  | "other";

function classifyOperation(detail: string): OperationType {
  const d = detail.toUpperCase();
  if (d.includes("AUTOMATIC COVERING INDEX")) return "auto_index";
  if (d.includes("USING COVERING INDEX")) return "search_covering";
  if (d.includes("USING INDEX")) return "search_index";
  if (d.startsWith("SCAN")) return "scan";
  if (d.startsWith("SEARCH")) return "search_index";
  if (d.includes("MATERIALIZE")) return "materialize";
  if (d.includes("CO-ROUTINE")) return "coroutine";
  if (d.includes("USE TEMP B-TREE")) return "temp_btree";
  if (d.includes("COMPOUND")) return "compound";
  return "other";
}

const OP_STYLES: Record<
  OperationType,
  { color: string; bg: string; label: string; tip: string }
> = {
  scan: {
    color: "#b71c1c",
    bg: "#ffebee",
    label: "SCAN",
    tip: "Full table scan with no index. This reads every row in the table. Consider adding an index on the filtered or joined columns."
  },
  search_index: {
    color: "#2e7d32",
    bg: "#e8f5e9",
    label: "INDEX",
    tip: "Uses an index to look up a subset of rows. Efficient for selective queries."
  },
  search_covering: {
    color: "#1b5e20",
    bg: "#c8e6c9",
    label: "COVERING",
    tip: "Uses a covering index that contains all needed columns. Best-case scenario — no table lookup required."
  },
  auto_index: {
    color: "#e65100",
    bg: "#fff3e0",
    label: "AUTO INDEX",
    tip: "SQLite created a temporary index on the fly. This adds overhead per query. Consider adding a persistent composite index."
  },
  materialize: {
    color: "#1565c0",
    bg: "#e3f2fd",
    label: "MATERIALIZE",
    tip: "A CTE or subquery is materialized into a temporary table. Normal for WITH clauses."
  },
  coroutine: {
    color: "#42a5f5",
    bg: "#e3f2fd",
    label: "CO-ROUTINE",
    tip: "A CTE evaluated lazily as a coroutine instead of being materialized. Can be more efficient for large result sets."
  },
  temp_btree: {
    color: "#e65100",
    bg: "#fff3e0",
    label: "TEMP B-TREE",
    tip: "A temporary B-tree is used for sorting (ORDER BY), grouping (GROUP BY), or deduplication (DISTINCT). Expected for these operations when no index covers the sort order."
  },
  compound: {
    color: "#616161",
    bg: "#f5f5f5",
    label: "COMPOUND",
    tip: "A compound query (UNION, UNION ALL, INTERSECT, or EXCEPT)."
  },
  other: {
    color: "#616161",
    bg: "#f5f5f5",
    label: "OTHER",
    tip: "Query plan operation."
  }
};

function buildPlanTree(rows: PlanRow[]): PlanTreeNode[] {
  const nodeMap = new Map<number, PlanTreeNode>();
  for (const row of rows) {
    nodeMap.set(row.id, { ...row, children: [], depth: 0 });
  }
  const roots: PlanTreeNode[] = [];
  for (const node of nodeMap.values()) {
    const parentNode = nodeMap.get(node.parent);
    if (parentNode && parentNode !== node) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  }
  function setDepth(nodes: PlanTreeNode[], depth: number) {
    for (const n of nodes) {
      n.depth = depth;
      setDepth(n.children, depth + 1);
    }
  }
  setDepth(roots, 0);
  return roots;
}

function PlanNodeRow({
  node,
  expanded,
  onToggle
}: {
  node: PlanTreeNode;
  expanded: boolean;
  onToggle: () => void;
}) {
  const op = classifyOperation(node.detail);
  const style = OP_STYLES[op];
  const hasChildren = node.children.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        pl: node.depth * 3,
        py: 0.4,
        "&:hover": { backgroundColor: "action.hover" },
        borderRadius: 0.5
      }}
    >
      <Box
        sx={{ width: 24, flexShrink: 0, cursor: hasChildren ? "pointer" : "default" }}
        onClick={hasChildren ? onToggle : undefined}
      >
        {hasChildren &&
          (expanded ? (
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          ))}
      </Box>
      <Tooltip title={style.tip} arrow placement="top-start">
        <Chip
          label={style.label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 700,
            color: style.color,
            backgroundColor: style.bg,
            mr: 1,
            cursor: "help",
            minWidth: 80
          }}
        />
      </Tooltip>
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "nowrap" }}
      >
        {node.detail}
      </Typography>
    </Box>
  );
}

function formatExplainText(rows: ExplainRow[]): string {
  return rows
    .map((r) => {
      const parts = [
        String(r.addr).padStart(4),
        r.opcode.padEnd(16),
        String(r.p1).padStart(4),
        String(r.p2).padStart(4),
        String(r.p3).padStart(4),
        (r.p4 || "").padEnd(20),
        r.p5 || "",
        r.comment ? `; ${r.comment}` : ""
      ];
      return parts.join("  ").trimEnd();
    })
    .join("\n");
}

function formatQueryPlanText(plan: PlanRow[]): string {
  const tree = buildPlanTree(plan);
  const lines: string[] = [];
  function walk(nodes: PlanTreeNode[], prefix: string, isLast: boolean[]) {
    nodes.forEach((node, i) => {
      const last = i === nodes.length - 1;
      const connector = prefix ? (last ? "`--" : "|--") : "";
      lines.push(`${prefix}${connector}${node.detail}`);
      const childPrefix = prefix + (prefix ? (last ? "   " : "|  ") : "");
      walk(node.children, childPrefix, [...isLast, last]);
    });
  }
  walk(tree, "", []);
  return lines.join("\n");
}

export function QueryPlanExplorer({
  plan,
  explainRows,
  sql
}: {
  plan: PlanRow[];
  explainRows: ExplainRow[];
  sql: string;
}) {
  const tree = buildPlanTree(plan);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState(0);

  const toggleNode = useCallback((id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function renderNodes(nodes: PlanTreeNode[]): React.ReactNode[] {
    const result: React.ReactNode[] = [];
    for (const node of nodes) {
      const isExpanded = !collapsed.has(node.id);
      result.push(
        <PlanNodeRow
          key={node.id}
          node={node}
          expanded={isExpanded}
          onToggle={() => toggleNode(node.id)}
        />
      );
      if (isExpanded && node.children.length > 0) {
        result.push(...renderNodes(node.children));
      }
    }
    return result;
  }

  const scanCount = plan.filter((r) => classifyOperation(r.detail) === "scan").length;
  const autoIndexCount = plan.filter((r) => classifyOperation(r.detail) === "auto_index").length;
  const tempBtreeCount = plan.filter((r) => classifyOperation(r.detail) === "temp_btree").length;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: "16px !important" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon />
            <Typography variant="h6">Query Plan</Typography>
            <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
              {scanCount > 0 && (
                <Chip label={`${scanCount} SCAN${scanCount > 1 ? "s" : ""}`} size="small"
                  sx={{ backgroundColor: "#ffebee", color: "#b71c1c", fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
              )}
              {autoIndexCount > 0 && (
                <Chip label={`${autoIndexCount} AUTO INDEX`} size="small"
                  sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
              )}
              {tempBtreeCount > 0 && (
                <Chip label={`${tempBtreeCount} TEMP B-TREE`} size="small"
                  sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
              )}
              {scanCount === 0 && autoIndexCount === 0 && tempBtreeCount === 0 && (
                <Chip label="All indexed" size="small"
                  sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
              )}
            </Stack>
          </Stack>
        </Stack>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 36, mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Tab label={`EXPLAIN QUERY PLAN (${plan.length})`}
            sx={{ minHeight: 36, textTransform: "none", fontSize: "0.8rem" }} />
          <Tab label={`EXPLAIN (${explainRows.length})`}
            sx={{ minHeight: 36, textTransform: "none", fontSize: "0.8rem" }} />
          <Tab label="SQL" sx={{ minHeight: 36, textTransform: "none", fontSize: "0.8rem" }} />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Copy query plan">
              <IconButton size="small"
                onClick={() => navigator.clipboard.writeText(formatQueryPlanText(plan))}
                sx={{ position: "absolute", top: 4, right: 20, zIndex: 1 }}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 500, overflowY: "auto" }}>
              {renderNodes(tree)}
            </Paper>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Copy bytecodes">
              <IconButton size="small"
                onClick={() => navigator.clipboard.writeText(
                  `addr  opcode            p1    p2    p3    p4                    p5  comment\n${"─".repeat(90)}\n${formatExplainText(explainRows)}`
                )}
                sx={{ position: "absolute", top: 4, right: 20, zIndex: 3 }}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Paper variant="outlined" sx={{ maxHeight: 500, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>Addr</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>Opcode</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }} align="right">P1</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }} align="right">P2</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }} align="right">P3</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>P4</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {explainRows.map((row) => (
                    <TableRow key={row.addr} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }}>{row.addr}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3, fontWeight: 600 }}>{row.opcode}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }} align="right">{row.p1}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }} align="right">{row.p2}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }} align="right">{row.p3}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{row.p4}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.65rem", py: 0.3, color: "text.secondary" }}>{row.comment}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Copy SQL">
              <IconButton size="small"
                onClick={() => navigator.clipboard.writeText(sql)}
                sx={{ position: "absolute", top: 4, right: 20, zIndex: 1, color: "#999" }}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Box component="pre" sx={{
              fontSize: "0.7rem", fontFamily: "monospace", backgroundColor: "#1e1e1e", color: "#d4d4d4",
              p: 2, borderRadius: 1, overflow: "auto", maxHeight: 500, whiteSpace: "pre-wrap", wordBreak: "break-all"
            }}>
              {sql}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
